<template>
  <div v-if="enabled">
    <!-- Floating Accessibility Button -->
    <button
      ref="floatingButton"
      :class="['accessibility-button', { 'accessibility-button--active': isAnalyzing }]"
      :style="buttonStyles"
      @click="runAnalysis"
      @mouseenter="buttonHover = true"
      @mouseleave="buttonHover = false"
      :title="buttonTitle"
      :disabled="isAnalyzing"
    >
      <span v-if="!isAnalyzing">{{ buttonText }}</span>
      <div v-else class="accessibility-button__spinner"></div>
    </button>

    <!-- Results Overlay -->
    <div
      v-if="showResults"
      class="accessibility-overlay"
      @click="closeOverlay"
    >
      <div
        class="accessibility-overlay__content"
        @click.stop
      >
        <!-- Header -->
        <div class="accessibility-overlay__header">
          <h2 class="accessibility-overlay__title">
            {{ resultsTitle }}
          </h2>
          <button
            class="accessibility-overlay__close"
            @click="closeOverlay"
            aria-label="Close accessibility report"
          >
            ×
          </button>
        </div>

        <!-- Summary Stats -->
        <div class="accessibility-overlay__summary">
          <div class="accessibility-stat accessibility-stat--success">
            <strong>{{ passedItems.length }}</strong>
            <span>&nbsp;elements pass WCAG AA</span>
          </div>
          <div class="accessibility-stat accessibility-stat--error">
            <strong>{{ failedItems.length }}</strong>
            <span>&nbsp;elements fail WCAG AA</span>
          </div>
          <div class="accessibility-stat accessibility-stat--info">
            <strong>{{ failedItemsGrouped.length }}</strong>
            <span>&nbsp;unique color combinations failing</span>
          </div>
        </div>

        <!-- Issues List -->
        <div v-if="failedItemsGrouped.length > 0" class="accessibility-overlay__issues">
          <h3 class="accessibility-overlay__issues-title">
            ⚠️ Color Combinations Failing WCAG AA (in Game Content)
          </h3>
          
          <div class="accessibility-overlay__issues-list">
            <div
              v-for="(group, index) in failedItemsGrouped"
              :key="index"
              class="accessibility-issue-group"
            >
              <!-- Prominent Element Identification -->
              <div class="accessibility-issue-group__element-header">
                <div class="accessibility-element-name">
                  <span class="accessibility-element-name__label">🎯 Element{{ group.elements.length > 1 ? 's' : '' }} with Issue:</span>
                  <span class="accessibility-element-name__value">{{ group.displayName }}</span>
                </div>
                <div class="accessibility-issue-group__count">
                  <div class="accessibility-count-badge">
                    {{ group.elements.length }} {{ group.elements.length === 1 ? 'element' : 'elements' }}
                  </div>
                  <div class="accessibility-count-detail">
                    <span v-if="group.normalTextCount > 0">{{ group.normalTextCount }} normal</span>
                    <span v-if="group.largeTextCount > 0">{{ group.largeTextCount }} large</span>
                  </div>
                </div>
              </div>

              <div class="accessibility-issue-group__header">
                <div class="accessibility-issue-group__info">
                  <div class="accessibility-issue-group__ratio">
                    <strong>Current Contrast Ratio: {{ group.ratio }}:1</strong>
                    <span class="accessibility-issue-group__required">
                      (Required: {{ group.requiredRatio }}:1)
                    </span>
                  </div>
                  <div class="accessibility-issue-group__colors">
                    <div class="accessibility-color">
                      <span class="accessibility-color__label">Current Text Color:</span>
                      <span
                        class="accessibility-color__swatch"
                        :style="{
                          backgroundColor: `rgb(${group.color.join(', ')})`,
                          color: getSwatchTextColor(group.color)
                        }"
                      >
                        {{ rgbToHex(group.color) }}<br>
                        rgb({{ group.color.join(', ') }})
                      </span>
                    </div>
                    <div class="accessibility-color">
                      <span class="accessibility-color__label">Current Background:</span>
                      <span
                        class="accessibility-color__swatch"
                        :style="{
                          backgroundColor: `rgb(${group.backgroundColor.join(', ')})`,
                          color: getSwatchTextColor(group.backgroundColor)
                        }"
                      >
                        {{ rgbToHex(group.backgroundColor) }}<br>
                        rgb({{ group.backgroundColor.join(', ') }})
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Suggested Fixes -->
              <div class="accessibility-suggestions">
                <h4 class="accessibility-suggestions__title">💡 Recommended Changes for "{{ group.displayName }}":</h4>
                <div class="accessibility-suggestions__list">
                  <div
                    v-for="(suggestion, suggestionIndex) in group.suggestions"
                    :key="suggestionIndex"
                    class="accessibility-suggestion"
                  >
                    <div class="accessibility-suggestion__header">
                      <span class="accessibility-suggestion__type">✨ {{ suggestion.description }}</span>
                      <span class="accessibility-suggestion__ratio">New ratio: {{ suggestion.ratio.toFixed(2) }}:1 ✓</span>
                    </div>
                    <div class="accessibility-suggestion__colors">
                      <div class="accessibility-suggestion__before">
                        <span class="accessibility-color__label">Current Color:</span>
                        <span
                          class="accessibility-color__swatch"
                          :style="{
                            backgroundColor: `rgb(${suggestion.originalColor.join(', ')})`,
                            color: getSwatchTextColor(suggestion.originalColor)
                          }"
                        >
                          {{ rgbToHex(suggestion.originalColor) }}<br>
                          rgb({{ suggestion.originalColor.join(', ') }})
                        </span>
                      </div>
                      <div class="accessibility-suggestion__arrow">→</div>
                      <div class="accessibility-suggestion__after">
                        <span class="accessibility-color__label">Suggested Color:</span>
                        <span
                          class="accessibility-color__swatch"
                          :style="{
                            backgroundColor: `rgb(${suggestion.suggestedColor.join(', ')})`,
                            color: getSwatchTextColor(suggestion.suggestedColor)
                          }"
                        >
                          {{ rgbToHex(suggestion.suggestedColor) }}<br>
                          rgb({{ suggestion.suggestedColor.join(', ') }})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Elements List (collapsible) -->
              <div class="accessibility-elements-list">
                <div
                  v-for="(element, elementIndex) in group.elements"
                  :key="elementIndex"
                  class="accessibility-element"
                >
                  <div class="accessibility-element__content">
                    <div class="accessibility-element__text">
                      "{{ element.text }}"
                    </div>
                    <div class="accessibility-element__meta">
                      <span class="accessibility-element__tag">{{ element.element.tagName.toLowerCase() }}</span>
                      <span class="accessibility-element__size">{{ element.isLarge ? 'large' : 'normal' }} text</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <div v-else class="accessibility-overlay__success">
          🎉 Excellent! All text elements in the game pass WCAG AA contrast requirements.
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccessibilityOverlay',
  props: {
    enabled: {
      type: Boolean,
      default: true
    },
    buttonText: {
      type: String,
      default: '🔍 Check Colors'
    },
    buttonTitle: {
      type: String,
      default: 'Analyze color contrast in game content'
    },
    resultsTitle: {
      type: String,
      default: 'Game Color Contrast Analysis'
    },
    iframeSelector: {
      type: String,
      default: 'iframe'
    },
    gameConfiguration: {
      type: Object,
      default: null
    },
    gameType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isAnalyzing: false,
      showResults: false,
      buttonHover: false,
      passedItems: [],
      failedItems: [],
      failedItemsGrouped: []
    }
  },
  computed: {
    buttonStyles() {
      return {
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: '999998',
        padding: '12px 16px',
        backgroundColor: this.buttonHover ? '#0056b3' : '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: this.isAnalyzing ? 'not-allowed' : 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s ease'
      }
    }
  },
  methods: {
    async runAnalysis() {
      if (this.isAnalyzing) return

      this.isAnalyzing = true
      this.passedItems = []
      this.failedItems = []
      this.failedItemsGrouped = []

      try {
        // Check if we have game configuration data to analyze
        if (this.gameConfiguration && this.gameType) {
          await this.analyzeGameConfiguration()
        } else {
          // Fallback to iframe analysis
          await this.analyzeIframeContent()
        }
        
        this.groupFailedItems()
        this.showResults = true

      } catch (error) {
        console.error('Accessibility analysis error:', error)
        this.showCrossOriginMessage()
      } finally {
        this.isAnalyzing = false
      }
    },

    async analyzeGameConfiguration() {
      // First analyze shared components that are common to ALL game types
      await this.analyzeSharedComponents()
      
      // Then analyze game-type-specific components
      if (this.gameType === 'WheelOfFortune') {
        await this.analyzeWheelOfFortune()
      } else if (this.gameType === 'ScratchAndWin') {
        await this.analyzeScratchAndWin()
      } else if (this.gameType === 'SlotMachine') {
        await this.analyzeSlotMachine()
      } else if (this.gameType === 'Tetris') {
        await this.analyzeTetris()
      } else if (this.gameType === 'MemoryGame') {
        await this.analyzeMemoryGame()
      } else {
        // For other game types, show a message that analysis is not yet supported
        this.showUnsupportedGameTypeMessage()
      }
    },

    async analyzeSharedComponents() {
      const config = this.gameConfiguration
      if (!config) return

      // Analyze timer/countdown colors (shared across all games) - only if timer is shown
      if (config.showTimer !== false) {
        if (config.timerTextColor && config.timerBackgroundColor) {
          this.analyzeColorPair(
            'Timer/Countdown',
            config.timerTextColor,
            config.timerBackgroundColor,
            'Timer text',
            16 // Default timer font size
          )
        }

        if (config.timerTopTextColor && config.timerTopBackgroundColor) {
          this.analyzeColorPair(
            'Timer Top Section',
            config.timerTopTextColor,
            config.timerTopBackgroundColor,
            'Timer top text',
            16
          )
        }
      }

      // Analyze action buttons (post-game buttons, shared across all games)
      if (config.actions && Array.isArray(config.actions)) {
        config.actions.forEach((action, index) => {
          // Action text colors
          if (action.textColor && action.textBackgroundColor) {
            this.analyzeColorPair(
              `Action ${index + 1} Text`,
              action.textColor,
              action.textBackgroundColor,
              `Action text`,
              config.fontSize || 14
            )
          }

          // Action button colors
          if (action.buttonTextColor && action.buttonBackgroundColor) {
            this.analyzeColorPair(
              `Action ${index + 1} Button`,
              action.buttonTextColor,
              action.buttonBackgroundColor,
              `Button text`,
              config.fontSize || 14
            )
          }
        })
      }

      // Analyze main background if there's text content over it
      if (config.backgroundColor) {
        // We'll analyze this against common text colors if we find any text elements
        // This is a placeholder for potential main background analysis
      }
    },

    async analyzeWheelOfFortune() {
      const config = this.gameConfiguration.wheelOfFortune
      if (!config) {
        console.warn('No WheelOfFortune configuration found')
        return
      }

      // Analyze spin button colors
      if (config.spinButtonTextColor && config.spinButtonBackgroundColor) {
        this.analyzeColorPair(
          'Spin Button',
          config.spinButtonTextColor,
          config.spinButtonBackgroundColor,
          'Button text',
          config.spinButtonFontSize || 16
        )
      }

      // Analyze wheel text color against various backgrounds
      if (config.wheelTextColor) {
        // Against center background
        if (config.centerBackgroundColor) {
          this.analyzeColorPair(
            'Wheel Center Text',
            config.wheelTextColor,
            config.centerBackgroundColor,
            'Center text',
            config.segmentsFontSize || 14
          )
        }

        // Against peg background
        if (config.pegBackgroundColor) {
          this.analyzeColorPair(
            'Wheel Text on Pegs',
            config.wheelTextColor,
            config.pegBackgroundColor,
            'Peg text',
            config.segmentsFontSize || 14
          )
        }
      }

      // Analyze individual wheel segments
      if (config.items && Array.isArray(config.items)) {
        config.items.forEach((item, index) => {
          if (item.foregroundColor && item.backgroundColor && item.text) {
            this.analyzeColorPair(
              `Wheel Segment ${index + 1}`,
              item.foregroundColor,
              item.backgroundColor,
              `"${item.text.substring(0, 30)}${item.text.length > 30 ? '...' : ''}"`,
              config.segmentsFontSize || 14
            )
          }
        })
      }
    },

    async analyzeScratchAndWin() {
      const config = this.gameConfiguration.scratchAndWin
      if (!config) return

      // Analyze scratch and win specific colors
      if (config.keepScratchingTextColor) {
        // We need a background color to compare against - could be main background
        if (this.gameConfiguration.backgroundColor) {
          this.analyzeColorPair(
            'Scratch Text',
            config.keepScratchingTextColor,
            this.gameConfiguration.backgroundColor,
            'Keep scratching text',
            this.gameConfiguration.fontSize || 14
          )
        }
      }

      // Additional ScratchAndWin-specific analysis can be added here
    },

    async analyzeSlotMachine() {
      const config = this.gameConfiguration.slotMachine
      if (!config) return

      // Analyze slot machine spin button colors
      if (config.spinButtonTextColor && config.spinButtonBackgroundColor) {
        this.analyzeColorPair(
          'Slot Machine Spin Button',
          config.spinButtonTextColor,
          config.spinButtonBackgroundColor,
          'Spin button text',
          config.spinButtonFontSize || this.gameConfiguration.fontSize || 16
        )
      }

      // Additional SlotMachine-specific analysis can be added here
    },

    async analyzeTetris() {
      const config = this.gameConfiguration.tetris
      if (!config) return

      // Analyze tetris end game button colors
      if (config.endGameButtonTextColor && config.endGameButtonBackgroundColor) {
        this.analyzeColorPair(
          'Tetris End Game Button',
          config.endGameButtonTextColor,
          config.endGameButtonBackgroundColor,
          'End game button text',
          this.gameConfiguration.fontSize || 16
        )
      }

      // Additional Tetris-specific analysis can be added here
    },

    async analyzeMemoryGame() {
      const config = this.gameConfiguration.memoryGame
      if (!config) {
        console.warn('No MemoryGame configuration found')
        return
      }

      // Note: MemoryGame primarily uses images for cards, heading, and footer
      // The main text-based elements are handled in shared components (timer, action buttons)
      // Since MemoryGame relies heavily on images rather than text with color combinations,
      // most accessibility concerns are addressed through alt text and shared components
      
      // Currently, there are no specific color combinations in MemoryGame configuration
      // that require contrast analysis beyond the shared components (timer, action buttons)
      // which are already analyzed in analyzeSharedComponents()
    },

    analyzeColorPair(elementName, foregroundColor, backgroundColor, description, fontSize = 14) {
      try {
        const textColor = this.parseColorString(foregroundColor)
        const bgColor = this.parseColorString(backgroundColor)
        
        if (!textColor || !bgColor) {
          console.warn(`Could not parse colors for ${elementName}:`, foregroundColor, backgroundColor)
          return
        }

        const contrast = this.calculateContrast(textColor, bgColor)
        const isLarge = fontSize >= 18
        const requiredRatio = isLarge ? 3 : 4.5
        const passed = contrast >= requiredRatio

        const analysis = {
          element: { tagName: 'GAME_ELEMENT' },
          text: elementName, // Use elementName instead of generic description
          color: textColor,
          backgroundColor: bgColor,
          contrast,
          requiredRatio,
          isLarge,
          passed,
          elementName
        }

        if (passed) {
          this.passedItems.push(analysis)
        } else {
          this.failedItems.push(analysis)
        }
      } catch (error) {
        console.warn(`Error analyzing ${elementName}:`, error)
      }
    },

    parseColorString(colorString) {
      if (!colorString) return null
      
      // Handle hex colors
      if (colorString.startsWith('#')) {
        const hex = colorString.slice(1)
        if (hex.length === 3) {
          return [
            parseInt(hex[0] + hex[0], 16),
            parseInt(hex[1] + hex[1], 16),
            parseInt(hex[2] + hex[2], 16)
          ]
        } else if (hex.length === 6) {
          return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16)
          ]
        } else if (hex.length === 8) {
          // Handle 8-character hex colors (RRGGBBAA) - ignore alpha channel
          return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16)
          ]
        }
      }
      
      // Handle rgb/rgba
      const rgbMatch = colorString.match(/rgba?\(([^)]+)\)/)
      if (rgbMatch) {
        const values = rgbMatch[1].split(',').map(v => parseFloat(v.trim()))
        return values.length >= 3 ? values.slice(0, 3) : null
      }
      
      // Handle named colors (basic set)
      const namedColors = {
        'white': [255, 255, 255],
        'black': [0, 0, 0],
        'red': [255, 0, 0],
        'green': [0, 128, 0],
        'blue': [0, 0, 255],
        'yellow': [255, 255, 0],
        'cyan': [0, 255, 255],
        'magenta': [255, 0, 255],
        'gray': [128, 128, 128],
        'grey': [128, 128, 128]
      }
      
      const lowerColor = colorString.toLowerCase()
      if (namedColors[lowerColor]) {
        return namedColors[lowerColor]
      }
      
      return null
    },

    async analyzeIframeContent() {
      // Find the iframe element
      const iframe = document.querySelector(this.iframeSelector)
      if (!iframe) {
        console.warn('No iframe found for accessibility analysis')
        this.showResults = true
        return
      }

      // Try to access iframe content
      let iframeDocument
      try {
        iframeDocument = iframe.contentDocument || iframe.contentWindow.document
      } catch (error) {
        console.warn('Cannot access iframe content due to cross-origin restrictions:', error)
        // Show a message about cross-origin limitations
        this.showCrossOriginMessage()
        return
      }

      if (!iframeDocument) {
        console.warn('Cannot access iframe document')
        this.showCrossOriginMessage()
        return
      }

      // Analyze the iframe content
      await this.analyzeDocument(iframeDocument)
    },

    showCrossOriginMessage() {
      // Create a special message for cross-origin iframe limitations
      this.failedItemsGrouped = [{
        ratio: 0,
        requiredRatio: 4.5,
        color: [255, 0, 0],
        backgroundColor: [255, 255, 255],
        elements: [{
          text: 'Cross-origin iframe detected - cannot analyze game content directly',
          element: { tagName: 'INFO' },
          isLarge: false
        }],
        normalTextCount: 1,
        largeTextCount: 0,
        suggestions: [{
          description: 'Manual inspection required',
          ratio: 0,
          originalColor: [255, 0, 0],
          suggestedColor: [0, 0, 0]
        }]
      }]
      this.failedItems = this.failedItemsGrouped[0].elements
      this.passedItems = []
      this.showResults = true
    },

    showUnsupportedGameTypeMessage() {
      // Create a message for unsupported game types
      this.failedItemsGrouped = [{
        ratio: 0,
        requiredRatio: 4.5,
        color: [255, 165, 0],
        backgroundColor: [255, 255, 255],
        elements: [{
          text: `Game type "${this.gameType}" - shared components analyzed, but game-specific analysis not yet implemented. Supported: WheelOfFortune, ScratchAndWin, SlotMachine, Tetris, MemoryGame.`,
          element: { tagName: 'INFO' },
          isLarge: false
        }],
        normalTextCount: 1,
        largeTextCount: 0,
        suggestions: [{
          description: 'Shared components (timer, action buttons) are analyzed',
          ratio: 0,
          originalColor: [255, 165, 0],
          suggestedColor: [0, 0, 0]
        }]
      }]
      this.failedItems = this.failedItemsGrouped[0].elements
      this.passedItems = []
      this.showResults = true
    },

    async analyzeDocument(doc) {
      // Get all text elements from the iframe document
      const textElements = this.getTextElements(doc)
      
      for (const element of textElements) {
        const analysis = this.analyzeElement(element)
        if (analysis) {
          if (analysis.passed) {
            this.passedItems.push(analysis)
          } else {
            this.failedItems.push(analysis)
          }
        }
      }
    },

    getTextElements(doc) {
      const elements = []
      const walker = doc.createTreeWalker(
        doc.body || doc.documentElement,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            const text = node.textContent.trim()
            if (text.length === 0) return NodeFilter.FILTER_REJECT
            
            const parent = node.parentElement
            if (!parent) return NodeFilter.FILTER_REJECT
            
            const style = doc.defaultView.getComputedStyle(parent)
            if (style.display === 'none' || style.visibility === 'hidden') {
              return NodeFilter.FILTER_REJECT
            }
            
            return NodeFilter.FILTER_ACCEPT
          }
        }
      )

      let node
      while (node = walker.nextNode()) {
        elements.push(node.parentElement)
      }

      return [...new Set(elements)] // Remove duplicates
    },

    analyzeElement(element) {
      try {
        const doc = element.ownerDocument
        const style = doc.defaultView.getComputedStyle(element)
        const text = element.textContent.trim()
        
        if (!text) return null

        const textColor = this.parseColor(style.color)
        const backgroundColor = this.getBackgroundColor(element)
        
        if (!textColor || !backgroundColor) return null

        const contrast = this.calculateContrast(textColor, backgroundColor)
        const fontSize = parseFloat(style.fontSize)
        const fontWeight = style.fontWeight
        const isLarge = this.isLargeText(fontSize, fontWeight)
        const requiredRatio = isLarge ? 3 : 4.5
        const passed = contrast >= requiredRatio

        return {
          element,
          text: text.substring(0, 100) + (text.length > 100 ? '...' : ''),
          color: textColor,
          backgroundColor,
          contrast,
          requiredRatio,
          isLarge,
          passed
        }
      } catch (error) {
        console.warn('Error analyzing element:', error)
        return null
      }
    },

    getBackgroundColor(element) {
      let current = element
      const doc = element.ownerDocument
      
      while (current && current !== doc.body && current !== doc.documentElement) {
        const style = doc.defaultView.getComputedStyle(current)
        const bgColor = this.parseColor(style.backgroundColor)
        
        if (bgColor && bgColor[3] > 0) { // Has opacity
          return bgColor.slice(0, 3) // Return RGB only
        }
        
        current = current.parentElement
      }
      
      // Default to white background
      return [255, 255, 255]
    },

    parseColor(colorString) {
      if (!colorString) return null
      
      // Handle rgb/rgba
      const rgbMatch = colorString.match(/rgba?\(([^)]+)\)/)
      if (rgbMatch) {
        const values = rgbMatch[1].split(',').map(v => parseFloat(v.trim()))
        return values.length >= 3 ? values : null
      }
      
      // Handle hex colors
      if (colorString.startsWith('#')) {
        const hex = colorString.slice(1)
        if (hex.length === 3) {
          return [
            parseInt(hex[0] + hex[0], 16),
            parseInt(hex[1] + hex[1], 16),
            parseInt(hex[2] + hex[2], 16)
          ]
        } else if (hex.length === 6) {
          return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16)
          ]
        }
      }
      
      return null
    },

    calculateContrast(color1, color2) {
      const l1 = this.getLuminance(color1)
      const l2 = this.getLuminance(color2)
      const lighter = Math.max(l1, l2)
      const darker = Math.min(l1, l2)
      return (lighter + 0.05) / (darker + 0.05)
    },

    getLuminance([r, g, b]) {
      const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      })
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
    },

    isLargeText(fontSize, fontWeight) {
      const isNumericWeight = !isNaN(fontWeight)
      const isBold = isNumericWeight ? fontWeight >= 700 : 
                    ['bold', 'bolder', '700', '800', '900'].includes(fontWeight)
      
      return fontSize >= 24 || (fontSize >= 18.66 && isBold)
    },

    groupFailedItems() {
      const groups = new Map()
      
      this.failedItems.forEach(item => {
        const key = `${item.color.join(',')}-${item.backgroundColor.join(',')}`
        
        if (!groups.has(key)) {
          groups.set(key, {
            color: item.color,
            backgroundColor: item.backgroundColor,
            ratio: item.contrast.toFixed(2),
            requiredRatio: item.requiredRatio,
            elements: [],
            normalTextCount: 0,
            largeTextCount: 0,
            suggestions: this.generateSuggestions(item.color, item.backgroundColor, item.requiredRatio)
          })
        }
        
        const group = groups.get(key)
        group.elements.push(item)
        
        if (item.isLarge) {
          group.largeTextCount++
        } else {
          group.normalTextCount++
        }
      })
      
      // Add display name for each group
      groups.forEach(group => {
        group.displayName = this.getGroupDisplayName(group.elements)
      })
      
      this.failedItemsGrouped = Array.from(groups.values())
    },

    getGroupDisplayName(elements) {
      if (elements.length === 1) {
        return elements[0].text
      }
      
      // Check if all elements have similar names (like "Action 1 Button", "Action 2 Button")
      const elementNames = elements.map(el => el.text)
      
      // Check for Action buttons pattern
      const actionButtonPattern = /^Action \d+ Button$/
      if (elementNames.every(name => actionButtonPattern.test(name))) {
        return 'Action Buttons'
      }
      
      // Check for Wheel Segment pattern
      const wheelSegmentPattern = /^Wheel Segment \d+$/
      if (elementNames.every(name => wheelSegmentPattern.test(name))) {
        return 'Wheel Segments'
      }
      
      // Check for other numbered patterns
      const numberedPattern = /^(.+?) \d+$/
      const firstMatch = elementNames[0].match(numberedPattern)
      if (firstMatch) {
        const baseName = firstMatch[1]
        const basePattern = new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\d+$`)
        if (elementNames.every(name => basePattern.test(name))) {
          return `${baseName}s` // Pluralize the base name
        }
      }
      
      // Check if all elements have the exact same name
      if (elementNames.every(name => name === elementNames[0])) {
        return elementNames[0]
      }
      
      // Fallback: show first element name with count
      return `${elementNames[0]} (and ${elements.length - 1} similar)`
    },

    generateSuggestions(textColor, backgroundColor, requiredRatio) {
      const suggestions = []
      
      // Try darkening text
      const darkerText = this.adjustBrightness(textColor, -0.4)
      const darkerTextContrast = this.calculateContrast(darkerText, backgroundColor)
      if (darkerTextContrast >= requiredRatio) {
        suggestions.push({
          description: 'Darken text color',
          ratio: darkerTextContrast,
          originalColor: textColor,
          suggestedColor: darkerText
        })
      }
      
      // Try lightening text more aggressively if darkening didn't work
      if (suggestions.length === 0) {
        const lighterText = this.adjustBrightness(textColor, 0.4)
        const lighterTextContrast = this.calculateContrast(lighterText, backgroundColor)
        if (lighterTextContrast >= requiredRatio) {
          suggestions.push({
            description: 'Lighten text color',
            ratio: lighterTextContrast,
            originalColor: textColor,
            suggestedColor: lighterText
          })
        }
      }
      
      // Try lightening background
      const lighterBg = this.adjustBrightness(backgroundColor, 0.4)
      const lighterBgContrast = this.calculateContrast(textColor, lighterBg)
      if (lighterBgContrast >= requiredRatio) {
        suggestions.push({
          description: 'Lighten background color',
          ratio: lighterBgContrast,
          originalColor: backgroundColor,
          suggestedColor: lighterBg
        })
      }
      
      // Try darkening background if lightening didn't work
      if (suggestions.length <= 1) {
        const darkerBg = this.adjustBrightness(backgroundColor, -0.4)
        const darkerBgContrast = this.calculateContrast(textColor, darkerBg)
        if (darkerBgContrast >= requiredRatio) {
          suggestions.push({
            description: 'Darken background color',
            ratio: darkerBgContrast,
            originalColor: backgroundColor,
            suggestedColor: darkerBg
          })
        }
      }
      
      return suggestions.slice(0, 3) // Allow up to 3 suggestions
    },

    adjustBrightness(color, factor) {
      return color.map(c => {
        const adjusted = factor > 0 ? 
          c + (255 - c) * factor : 
          c + c * factor
        return Math.round(Math.max(0, Math.min(255, adjusted)))
      })
    },

    rgbToHex([r, g, b]) {
      return '#' + [r, g, b].map(c => 
        Math.round(c).toString(16).padStart(2, '0')
      ).join('')
    },

    getSwatchTextColor([r, g, b]) {
      const luminance = this.getLuminance([r, g, b])
      return luminance > 0.5 ? '#000000' : '#ffffff'
    },

    closeOverlay() {
      this.showResults = false
    }
  }
}
</script>

<style scoped>
.accessibility-button {
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  min-width: 120px;
}

.accessibility-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.accessibility-button--active {
  animation: pulse 2s infinite;
}

.accessibility-button__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.accessibility-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.accessibility-overlay__content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  width: 800px;
}

.accessibility-overlay__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.accessibility-overlay__title {
  margin: 0;
  color: #333;
  font-size: 24px;
}

.accessibility-overlay__close {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
}

.accessibility-overlay__close:hover {
  background: #c82333;
}

.accessibility-overlay__summary {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.accessibility-stat {
  padding: 16px;
  border-radius: 6px;
  border-left: 4px solid;
  flex: 1;
  min-width: 200px;
}

.accessibility-stat--success {
  background: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.accessibility-stat--error {
  background: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
}

.accessibility-stat--info {
  background: #d1ecf1;
  border-color: #17a2b8;
  color: #0c5460;
}

.accessibility-overlay__issues {
  margin-bottom: 24px;
}

.accessibility-overlay__issues-title {
  color: #dc3545;
  margin: 0 0 16px 0;
  font-size: 20px;
}

.accessibility-overlay__issues-list {
  max-height: 400px;
  overflow-y: auto;
}

.accessibility-issue-group {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin: 16px 0;
  background: #fff;
  overflow: hidden;
}

.accessibility-issue-group__element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fff3cd;
  border-bottom: 2px solid #ffc107;
}

.accessibility-element-name {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.accessibility-element-name__label {
  font-weight: 600;
  color: #856404;
  font-size: 16px;
}

.accessibility-element-name__value {
  font-weight: 700;
  color: #212529;
  font-size: 18px;
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ffc107;
}

.accessibility-issue-group__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.accessibility-issue-group__info {
  flex: 1;
}

.accessibility-issue-group__ratio {
  font-size: 16px;
  margin-bottom: 12px;
}

.accessibility-issue-group__required {
  color: #dc3545;
  margin-left: 12px;
  font-weight: normal;
}

.accessibility-issue-group__colors {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.accessibility-issue-group__count {
  text-align: right;
  flex-shrink: 0;
}

.accessibility-count-badge {
  background: #dc3545;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
}

.accessibility-count-detail {
  font-size: 12px;
  color: #6c757d;
}

.accessibility-count-detail span {
  margin-left: 8px;
}

.accessibility-elements-list {
  padding: 0;
}

.accessibility-element {
  border-bottom: 1px solid #f1f3f4;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.accessibility-element:last-child {
  border-bottom: none;
}

.accessibility-element:hover {
  background: #f8f9fa;
}

.accessibility-element__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.accessibility-element__text {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-style: italic;
  margin-right: 16px;
}

.accessibility-element__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
  flex-shrink: 0;
}

.accessibility-element__tag {
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
}

.accessibility-element__size {
  background: #fff3cd;
  padding: 2px 6px;
  border-radius: 3px;
  color: #856404;
}

.accessibility-color {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.accessibility-color__label {
  font-weight: 500;
}

.accessibility-color__swatch {
  padding: 8px 10px;
  border-radius: 4px;
  font-family: monospace;
  border: 1px solid #ccc;
  font-size: 11px;
  line-height: 1.3;
  text-align: center;
  min-width: 100px;
}

.accessibility-overlay__success {
  color: #28a745;
  font-weight: bold;
  font-size: 18px;
  text-align: center;
  padding: 24px;
  background: #d4edda;
  border-radius: 6px;
  margin-bottom: 24px;
}

.accessibility-overlay__info {
  padding: 16px;
  background: #e9ecef;
  border-radius: 6px;
}

.accessibility-overlay__info-title {
  margin: 0 0 12px 0;
  font-size: 16px;
}

.accessibility-overlay__info-list {
  margin: 0;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.5;
}

.accessibility-suggestions {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #28a745;
}

.accessibility-suggestions__title {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #28a745;
  font-weight: 600;
}

.accessibility-suggestions__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.accessibility-suggestion {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
}

.accessibility-suggestion__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.accessibility-suggestion__type {
  font-weight: 500;
  font-size: 13px;
  color: #495057;
}

.accessibility-suggestion__ratio {
  font-size: 12px;
  color: #28a745;
  font-weight: 600;
}

.accessibility-suggestion__colors {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.accessibility-suggestion__arrow {
  font-size: 24px;
  font-weight: bold;
  color: #28a745;
  margin: 0 8px;
}

.accessibility-suggestion__before,
.accessibility-suggestion__after {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.accessibility-suggestion__before .accessibility-color__label {
  color: #dc3545;
  font-weight: 500;
}

.accessibility-suggestion__after .accessibility-color__label {
  color: #28a745;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(0, 123, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 123, 255, 0); }
}
</style>
